import { useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { zodResolver } from '@hookform/resolvers/zod'

import { api } from '@/modules/shared/lib/axios'
import { useHttpClient } from '@/modules/shared/hooks/use-http-client'
import { useAuthStore } from '@/modules/shared/stores/auth.store'
import { useMainStore } from '@/modules/shared/stores/main.store'
import { env } from '@/modules/shared/infra/env'

import PackageJson from '../../../../package.json'
import { authPasswordService } from '../services/auth-password.service'
import { useLoginStore } from '../stores/login.store'
import { authPasswordSchema, AuthPasswordSchema } from '../schemas/auth-password.schema'

function parseBooleanParam(value: unknown) {
  return ['true', '1'].includes(String(value).toLowerCase())
}

export function useAuthPassword() {
  const navigate = useNavigate()

  const {
    actions: { setVersionUrl,setMetaPorQuantidade, setVersionApp, setToken, setUser, setChaveMaps, setNivelAcesso },
  } = useAuthStore()

  const {
    actions: { setConfirmation, setCloseConfirmation },
  } = useMainStore()

  const {
    companies,
    email,
    companySelected,
    setCompanySelected
  } = useLoginStore()

  const form = useForm<AuthPasswordSchema>({
    resolver: zodResolver(authPasswordSchema),
    values: {
      company: companies?.length > 0 ? companies[0].codigo : '',
      password: '',
    },
  })

  const httpClient = useHttpClient(
    authPasswordService,
    {
      onSuccess: (response) => {
        if (response.status === 200) {
          handleLoginSuccess(response)
        }
      },
      showToast: true,
			showSucessToast: false,
    }
  )

  const handleLoginSuccess = useCallback((response: any) => {
    const responseParse = JSON.parse(response.body)

    localStorage.setItem('companyCode', form.watch('company'))

    setUser({
      id: responseParse.id,
      nome: responseParse.nome,
      empresaNome: responseParse.empresaNome,
      vendedor: responseParse.vendedor,
      vendedorEmail: responseParse.vendedorEmail,
    })

    setMetaPorQuantidade(
      parseBooleanParam(
        responseParse?.MetaPorQuantidade ?? responseParse?.metaPorQuantidade,
      ),
    )
		const nivelAcesso = Number(responseParse.nivelAcesso ?? 1)

    setToken(responseParse.token)
		setNivelAcesso(Number.isFinite(nivelAcesso) ? nivelAcesso : 1)
    setChaveMaps(responseParse.chaveMaps)
    navigate('/')
  }, [companySelected, companies, setUser, setToken, setChaveMaps, navigate])

  const submitAuthPassword = useCallback(async (data: AuthPasswordSchema) => {
    const { company, password } = data

    const responseUrlApi = companies?.find(
      (item) => item.codigo === company,
    )

    if (env.VITE_API_URL_LOCKED) {
      api.defaults.baseURL = env.VITE_API_URL
    } else {
      api.defaults.baseURL = responseUrlApi?.url
    }

    setVersionUrl(responseUrlApi?.url)
    setVersionApp(PackageJson?.version)
    setCompanySelected(company)

    try {
      await httpClient.execute({
        payload: {
          company,
          password,
          email,
        },
      })
    } catch (error) {
      console.error('Erro no login:', error)
    }
  }, [companies, email, httpClient, setVersionUrl, setVersionApp, setCompanySelected])

  const warningToSelectCorrectlyCompany = useCallback(() => {
    const quantityOfCompany = companies?.length

    if (quantityOfCompany === 1) {
      setCompanySelected(form.watch('company'))
      navigate('/esqueci-minha-senha')
      return
    }

    setConfirmation({
      isOpen: true,
      message:
        'Você possui cadastro em mais de uma empresa, verifique se selecionou a empresa corretamente antes de prosseguir.',
      onConfirm: () => {
        navigate('/esqueci-minha-senha')
        setCloseConfirmation()
      },
      onClose: () => {
        setCloseConfirmation()
      },
    })
  }, [companies?.length, form, setCompanySelected, navigate, setConfirmation, setCloseConfirmation])

  useEffect(() => {
    if (!email) {
      toast.info('Por favor, realize o login novamente')
      navigate('/login')
    }
  }, [email, navigate])
	useEffect(() => {
		setCompanySelected(form.watch('company'))
	}, [form.watch('company')])

  return {
    form,
    isLoading: httpClient.isLoading,
    error: httpClient.error,
    companies,
    submitAuthPassword,
    warningToSelectCorrectlyCompany,
  }
}
